import { Card, Col, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from 'services/apiClient';

const ANNIVERSARY_WITHIN_DAYS = 60;

type OpportunitySummaryKey = 'upcomingProject' | 'renewableProposal';

type StatusSummaryCardItem =
  | {
      key: string;
      type: 'status';
      statusId: number;
      label: string;
      link: string;
    }
  | {
      key: string;
      type: 'plannedPayment';
      label: string;
      link: string;
    }
  | {
      key: string;
      type: 'opportunity';
      opportunityKey: OpportunitySummaryKey;
      label: string;
      link: string;
    };

type OpportunitySummaryCounts = Record<OpportunitySummaryKey, number>;

const STATUS_SUMMARY_CARDS: StatusSummaryCardItem[] = [
  { key: 'status10', type: 'status', statusId: 10, label: 'Onay Bekleyen\nSözleşme', link: '/project-followup?tab=1' },
  { key: 'status6', type: 'status', statusId: 6, label: 'Planlama\nBekleyen', link: '/project-followup?tab=2' },
  { key: 'status5', type: 'status', statusId: 5, label: 'Kontrol & Raporlama\nDevam Eden', link: '/project-followup?tab=4' },
  { key: 'status4', type: 'plannedPayment', label: 'Ödemesi Planlanmış\nProjeler', link: '/payments' },
  { key: 'upcomingProject', type: 'opportunity', opportunityKey: 'upcomingProject', label: 'Yaklaşan\nProje', link: '/opportunity?tab=1' },
  { key: 'renewableProposal', type: 'opportunity', opportunityKey: 'renewableProposal', label: 'Yenilenebilir\nTeklif', link: '/opportunity?tab=2' },
];

const emptyOpportunityCounts: OpportunitySummaryCounts = {
  upcomingProject: 0,
  renewableProposal: 0,
};

const StatusSummaryCards = () => {
  const [statusCounts, setStatusCounts] = useState<Record<number, number>>({});
  const [plannedPaymentCount, setPlannedPaymentCount] = useState<number>(0);
  const [opportunityCounts, setOpportunityCounts] = useState<OpportunitySummaryCounts>(emptyOpportunityCounts);

  useEffect(() => {
    let ignore = false;

    const loadStatusCounts = async () => {
      try {
        const response = await apiFetch('/proposals/report/status-counts/total');
        if (!ignore && response && typeof response === 'object') {
          setStatusCounts({
            4: response?.status4 ?? 0,
            5: response?.status5 ?? 0,
            6: response?.status6 ?? 0,
            7: response?.status7 ?? 0,
            10: response?.status10 ?? 0,
          });
        }
      } catch (error) {
        console.error('Dashboard status count fetch failed', error);
        if (!ignore) {
          setStatusCounts({});
        }
      }
    };

    loadStatusCounts();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadPlannedPaymentCount = async () => {
      try {
        const params = new URLSearchParams({
          page: '0',
          size: '10',
          accountId: '',
          companyId: '',
          company: '',
          city: '',
          district: '',
          statuIds: '4',
          isPaid: 'false',
          sort: 'invoiceDate',
          onlyPartner: 'true',
          direction: 'ASC',
        });
        const response = await apiFetch(`/proposals?${params.toString()}`);
        const total = Number(response?.totalElements ?? response?.content?.length ?? 0);

        if (!ignore) {
          setPlannedPaymentCount(Number.isFinite(total) ? total : 0);
        }
      } catch (error) {
        console.error('Dashboard planned payment fetch failed', error);
        if (!ignore) {
          setPlannedPaymentCount(0);
        }
      }
    };

    loadPlannedPaymentCount();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const getOpportunityCount = async (statuIds: string) => {
      const params = new URLSearchParams({
        page: '0',
        size: '1',
        renewable: 'true',
        anniversaryWithinDays: String(ANNIVERSARY_WITHIN_DAYS),
        statuIds,
        sort: 'renewalDate',
        direction: 'asc',
      });
      const response = await apiFetch(`/proposals?${params.toString()}`);
      const total = Number(response?.totalElements ?? response?.content?.length ?? 0);

      return Number.isFinite(total) ? total : 0;
    };

    const loadOpportunityCounts = async () => {
      try {
        const [upcomingProject, renewableProposal] = await Promise.all([
          getOpportunityCount('4'),
          getOpportunityCount('2'),
        ]);

        if (!ignore) {
          setOpportunityCounts({
            upcomingProject,
            renewableProposal,
          });
        }
      } catch (error) {
        console.error('Dashboard opportunity count fetch failed', error);
        if (!ignore) {
          setOpportunityCounts(emptyOpportunityCounts);
        }
      }
    };

    loadOpportunityCounts();

    return () => {
      ignore = true;
    };
  }, []);

  const getCardValue = (card: StatusSummaryCardItem) => {
    if (card.type === 'plannedPayment') {
      return plannedPaymentCount;
    }

    if (card.type === 'opportunity') {
      return opportunityCounts[card.opportunityKey] ?? 0;
    }

    return statusCounts[card.statusId] ?? 0;
  };

  return (
    <Row justify="space-between" gutter={[24, 24]} style={{ marginTop: '24px' }}>
      {STATUS_SUMMARY_CARDS.map((card) => {
        const cardValue = getCardValue(card);

        return (
          <Col key={card.key} xs={24} sm={12} md={8} lg={4}>
            <Link to={card.link} style={{ display: 'block', width: '100%' }}>
              <Card
                bordered={false}
                style={{
                  background: '#fff',
                  textAlign: 'center',
                  minHeight: 140,
                  width: '100%',
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    color: '#555',
                    minHeight: 60,
                    whiteSpace: 'pre-line',
                    fontWeight: 600,
                  }}
                >
                  {card.label}
                </div>
                <div
                  style={{
                    color: cardValue === 0 ? '#52c41a' : '#f5222d',
                    fontWeight: 700,
                    fontSize: 28,
                  }}
                >
                  {cardValue}
                </div>
              </Card>
            </Link>
          </Col>
        );
      })}
    </Row>
  );
};

export default StatusSummaryCards;
